import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { Sparkles, CheckCircle2 } from 'lucide-react';
import { useEntwurf } from '@/entwurf/EntwurfOptionen';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  ATTRIBUT_FELDER,
  deriveChanges,
  type AttributFeld,
  type ChangeInput,
  type ChangeMarker,
} from '@/lib/changeSummary';
import type { WorkbenchProduct, WorkbenchEditState } from '@/hooks/useWorkbench';

type TFunc = ReturnType<typeof useTranslation>['t'];

interface ChangeSummaryProps {
  product: WorkbenchProduct;
  editState: WorkbenchEditState;
  /**
   * Punkte-Badge rechts neben der Ueberschrift (Entwurfs-Schalter
   * `punkteOrt: 'imAenderungsblock'`). Der Score gehoert dann zu der Stelle,
   * die auch die Begruendung traegt. Gebaut vom FeedColumn-Aufrufer, weil
   * nur der die Score-Daten und den Auf/Zu-Zustand haelt.
   */
  scoreSlot?: ReactNode;
  /** Der aufgeklappte Breakdown unter der Ueberschrift, gleiche Quelle. */
  scorePanel?: ReactNode;
}

/**
 * Welches Feld von `WorkbenchEditState` zu welchem Attribut gehoert.
 *
 * Ausgeschrieben statt ueber einen Template-String `optimized_${feld}`: die
 * ausgeschriebene Fassung bricht beim Umbenennen eines Feldes im Typ, der
 * Template-Zugriff liefert dann still `undefined` - und ein still leeres
 * Attribut sieht auf dieser Karte wie ein fehlender Merchant-Wert aus.
 *
 * Exportiert, weil `AttributeChips` dieselbe Zuordnung braucht. Zwei Kopien
 * derselben Sieben-Zeilen-Tabelle driften auseinander.
 */
export const OPTIMIERT_KEY: Record<AttributFeld, keyof WorkbenchEditState> = {
  color: 'optimized_color',
  material: 'optimized_material',
  gender: 'optimized_gender',
  age_group: 'optimized_age_group',
  size: 'optimized_size',
  pattern: 'optimized_pattern',
  google_product_category: 'optimized_google_product_category',
};

/**
 * Baut aus Produkt und Bearbeitungsstand die Eingabe von `deriveChanges`.
 *
 * Der optimierte Wert kommt IMMER aus `editState`, nie aus `product`: nach
 * einer Handkorrektur soll die Liste beschreiben, was wirklich hochgeladen
 * wuerde. Exportiert, damit die Paarbildung eigenstaendig pruefbar ist, ohne
 * dafuer einen Renderbaum aufzubauen.
 */
export function changeInputFor(
  product: WorkbenchProduct,
  editState: WorkbenchEditState,
): ChangeInput {
  return {
    title: product.original_title ?? null,
    optimizedTitle: editState.optimized_title,
    brand: product.brand,
    attribute: ATTRIBUT_FELDER.map((feld: AttributFeld) => ({
      feld,
      original: (product[feld] as string | null) ?? null,
      optimiert: (editState[OPTIMIERT_KEY[feld]] as string | undefined) ?? null,
    })),
  };
}

function markerText(marker: ChangeMarker, t: TFunc): { titel: string; text: string } {
  switch (marker.id) {
    case 'produktart_vorn':
      return {
        titel: t('changes.produktart_vorn.titel'),
        text: t('changes.produktart_vorn.text', { alt: marker.alt, neu: marker.neu }),
      };
    case 'marke_vorn':
      return {
        titel: t('changes.marke_vorn.titel'),
        text: t('changes.marke_vorn.text', { marke: marker.marke }),
      };
    case 'attribut_ergaenzt': {
      // Die Spaltennamen sind fuer den Nutzer bedeutungslos; uebersetzt wird
      // ueber changes.felder.*, dieselbe Liste, die Etappe 1 angelegt hat.
      const felder = marker.felder.map((f) => t(`changes.felder.${f}`)).join(', ');
      // Die WERTE gehoeren in den Satz, nicht nur die Feldnamen: "Farbe,
      // Groesse ergaenzt" sagt DASS, "Navy, L" sagt WOMIT - und nur das
      // laesst sich pruefen, ohne den Blick zu den Chips wandern zu lassen.
      const werte = marker.werte.filter((w) => w !== '').join(', ');
      return {
        titel: t('changes.attribut_ergaenzt.titel', { felder }),
        text: werte
          ? t('changes.attribut_ergaenzt.textMitWerten', { werte })
          : t('changes.attribut_ergaenzt.text'),
      };
    }
    case 'masz_ergaenzt':
      return {
        titel: t('changes.masz_ergaenzt.titel'),
        text: t('changes.masz_ergaenzt.text', { treffer: marker.treffer }),
      };
    case 'floskel_entfernt':
      return {
        titel: t('changes.floskel_entfernt.titel'),
        text: t('changes.floskel_entfernt.text', { beispiel: marker.beispiel }),
      };
  }
}

/**
 * "Was hat sich geaendert": hoechstens vier benannte Marker.
 *
 * Kostet null Tokens, keinen LLM-Aufruf, keinen zusaetzlichen Request. Alle
 * Marker entstehen aus Daten, die der Werkbank-Query ohnehin laedt; die
 * Begruendungssaetze sind feste Textbausteine je Marker, nur die eingesetzten
 * Werte sind produktabhaengig.
 *
 * Gibt es keinen Marker, rendert die Komponente `null`. Ein leerer Abschnitt
 * mit Ueberschrift stuende auf jeder zweiten Karte und waere nach dem dritten
 * Produkt unsichtbar.
 */
export function ChangeSummary({ product, editState, scoreSlot, scorePanel }: ChangeSummaryProps) {
  const { t } = useTranslation('workbench');
  const { wasGeaendertIcon, wasGeaendertLayout } = useEntwurf();
  const marker = deriveChanges(changeInputFor(product, editState));
  const liste = wasGeaendertLayout === 'liste';
  const chips = wasGeaendertLayout === 'chips';

  // Ohne Marker UND ohne Score-Slot bleibt die Regel: nichts rendern. Mit
  // Slot muss die Sektion stehen, sonst verloere die Karte ihre Punktzahl.
  if (marker.length === 0 && !scoreSlot) return null;

  const heading = (
    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {t('changes.heading')}
    </h4>
  );

  return (
    <section className="space-y-2">
      {scoreSlot ? (
        <div className="flex items-center justify-between gap-2">
          {heading}
          {scoreSlot}
        </div>
      ) : (
        heading
      )}
      {scorePanel}
      {/* B8: eigener, lokaler `TooltipProvider` statt sich auf einen
          Vorfahren zu verlassen (`App.tsx` mountet zwar einen global, aber
          diese Komponente wird auch isoliert gerendert, z. B. in Tests, und
          Radix' `Tooltip` wirft ohne Provider-Vorfahren). Verschachtelte
          Provider sind in Radix unproblematisch — der innerste gewinnt. */}
      <TooltipProvider delayDuration={200}>
      <ul
        className={
          chips ? 'flex flex-wrap gap-1.5' : liste ? 'flex flex-col gap-1.5' : 'grid gap-2 sm:grid-cols-2'
        }
      >
        {marker.map((m) => {
          const { titel, text } = markerText(m, t);
          const icon =
            wasGeaendertIcon === 'haken' ? (
              // Gruener Haken = "Verbesserung umgesetzt"; das Sparkle
              // bleibt den Chips vorbehalten ("das kam von der KI").
              <CheckCircle2
                data-testid="change-icon-haken"
                className={`h-3.5 w-3.5 flex-shrink-0 text-success ${chips ? '' : 'mt-0.5'}`}
                aria-hidden="true"
              />
            ) : (
              <Sparkles
                data-testid="change-icon-sparkle"
                className={`h-3.5 w-3.5 flex-shrink-0 text-primary ${chips ? '' : 'mt-0.5'}`}
                aria-hidden="true"
              />
            );

          // Chip-Form (T5, B8): nur der `titel` ist SICHTBAR, der
          // Begruendungssatz `text` sitzt im Haus-Tooltip UND im `aria-label`
          // des Ausloesers - Farbe und Kuerzung sind fuer sich genommen keine
          // Information. `title` allein (nur Maus) reichte Tastaturnutzern
          // nie, deshalb der Tooltip mit einem NATIVEN <button> als Ausloeser
          // (Radix' `TooltipTrigger asChild` reicht Ref und Events an ihn
          // durch) - ein Button ist ohne erfundenes `tabIndex` im Tab-Index.
          // `aria-label`/`aria-describedby` bleibt der Weg fuer
          // Screenreader, `TooltipContent` deckt Maus UND Tastaturfokus ab.
          if (chips) {
            return (
              <li key={m.id} data-testid="change-marker">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label={`${titel}. ${text}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/20 px-2.5 py-1 text-xs"
                    >
                      {icon}
                      <span className="font-medium text-foreground">{titel}</span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{text}</TooltipContent>
                </Tooltip>
              </li>
            );
          }

          return (
            <li
              key={m.id}
              data-testid="change-marker"
              className={
                liste
                  ? 'flex items-start gap-2'
                  : 'flex items-start gap-2 rounded-md border border-border bg-muted/20 px-2.5 py-2'
              }
            >
              {icon}
              <span className="min-w-0 text-xs leading-snug">
                <span className="font-medium text-foreground">{titel}</span>
                <span className={liste ? 'text-muted-foreground' : 'block text-muted-foreground'}>
                  {liste ? <> {'·'} {text}</> : text}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
      </TooltipProvider>
    </section>
  );
}
